import React, { useEffect, useState } from 'react';

import { Button, Divider, Group, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconEdit,
  IconPlus,
  IconUnlink,
  IconUserDown,
  IconUserFilled,
  IconUserUp,
} from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useConnectedAthletes from 'hooks/athlete/connected/useConnectedAthletes';
import DrawerAthleteForm from 'pages/private/owner/Athletes/components/DrawerAthleteForm';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteRetrieve, ConnectedAthlete } from 'types';

import DependentToLinkedModal from './components/DependentToLinkedModal';
import DisconnectProfileModal from './components/DisconnectProfileModal';
import LinkedToDependentModal from './components/LinkedToDependentModal';
import MakePrimaryModal from './components/MakePrimaryModal';

const ConnectedProfilesCard = ({
  athlete,
  permission,
}: {
  athlete: AthleteRetrieve;
  permission?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [data, setData] = useState<ConnectedAthlete[]>([]);
  const [selectedAthlete, setSelectedAthlete] = useState<ConnectedAthlete | null>(null);

  const [
    isCreateLinkedProfileFormOpened,
    { open: openCreateLinkedProfileForm, close: closeCreateLinkedProfileForm },
  ] = useDisclosure(false);
  const [
    isCreateDependentProfileFormOpened,
    { open: openCreateDependentProfileForm, close: closeCreateDependentProfileForm },
  ] = useDisclosure(false);
  const [isMakePrimaryModalOpen, { open: openMakePrimaryModal, close: closeMakePrimaryModal }] =
    useDisclosure(false);
  const [
    isDependentToLinkedModalOpen,
    { open: openDependentToLinkedModal, close: closeDependentToLinkedModal },
  ] = useDisclosure(false);
  const [
    isLinkedToDependentModalOpen,
    { open: openLinkedToDependentModal, close: closeLinkedToDependentModal },
  ] = useDisclosure(false);
  const [isDisconnectModalOpen, { open: openDisconnectModal, close: closeDisconnectModal }] =
    useDisclosure(false);

  const { data: connectedAthletes, isSuccess } = useConnectedAthletes(athlete.athleteId);

  const handleNameClick = (connectedAthlete: ConnectedAthlete): void => {
    navigate(`/users/athletes/${connectedAthlete.athleteId}/general-info`);
  };

  const handleMakePrimary = (connectedAthlete: ConnectedAthlete): void => {
    openMakePrimaryModal();
    setSelectedAthlete(connectedAthlete);
  };

  const handleDependentToLinked = (connectedAthlete: ConnectedAthlete): void => {
    openDependentToLinkedModal();
    setSelectedAthlete(connectedAthlete);
  };

  const handleLinkedToDependent = (connectedAthlete: ConnectedAthlete): void => {
    openLinkedToDependentModal();
    setSelectedAthlete(connectedAthlete);
  };

  const handleDisconnect = (connectedAthlete: ConnectedAthlete): void => {
    openDisconnectModal();
    setSelectedAthlete(connectedAthlete);
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<ConnectedAthlete>) => (
        <Text
          underline={row.original.athleteId !== athlete.athleteId}
          fw={row.original.athleteId === athlete.athleteId ? 'bold' : undefined}
          sx={row.original.athleteId !== athlete.athleteId ? { cursor: 'pointer' } : undefined}
          onClick={
            row.original.athleteId !== athlete.athleteId ? () => handleNameClick(row.original) : undefined
          }
          data-cy='connectedAthleteName'
        >
          {row.original.fullName}
        </Text>
      ),
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ row }: CellProps<ConnectedAthlete>) => (
        <Text sx={{ wordBreak: 'break-word' }}>{row.original.email}</Text>
      ),
    },
    {
      Header: 'Profile type',
      accessor: 'accountType',
      Cell: ({ row }: CellProps<ConnectedAthlete>) => (
        <>{row.original.isDependent ? 'Dependent' : row.original.primaryAccount ? 'Linked' : 'Primary'}</>
      ),
    },
    {
      id: 'actions',
      disableSortBy: true,
      Cell: ({ row }: CellProps<ConnectedAthlete>) => (
        <Group position='right' data-cy='connectedProfileActions'>
          <Menu
            disabled={!permission}
            items={[
              {
                title: 'View',
                icon: <IconEdit size={theme.fontSizes.sm} />,
                onClick: () => handleNameClick(row.original),
              },
              {
                title: 'Make Primary',
                icon: <IconUserFilled size={theme.fontSizes.sm} data-cy='makePrimaryIcon' />,
                onClick: () => handleMakePrimary(row.original),
                invisible: !row.original.primaryAccount || row.original.isDependent,
              },
              {
                title: 'Make Linked',
                icon: <IconUserUp size={theme.fontSizes.sm} data-cy='makeLinkedIcon' />,
                onClick: () => handleDependentToLinked(row.original),
                invisible: !row.original.isDependent,
              },
              {
                title: 'Make Dependent',
                icon: <IconUserDown size={theme.fontSizes.sm} data-cy='LinkedToDependentIcon' />,
                onClick: () => handleLinkedToDependent(row.original),
                invisible: row.original.isDependent || !row.original.primaryAccount,
              },
              {
                title: 'Disconnect',
                color: 'red',
                icon: <IconUnlink size={theme.fontSizes.sm} data-cy='disconnectIcon' />,
                onClick: () => handleDisconnect(row.original),
                invisible: !row.original.primaryAccount || row.original.isDependent,
              },
            ]}
          />
        </Group>
      ),
    },
  ];

  useEffect(() => {
    if (isSuccess && connectedAthletes.data) {
      const sortedData = connectedAthletes.data;
      sortedData.sort((a, b) => {
        if (!a.primaryAccount) return -2;
        if (!b.primaryAccount) return +2;
        if (a.isDependent && !b.isDependent) return +1;
        if (b.isDependent && !a.isDependent) return -1;
        return 0;
      });
      setData(connectedAthletes.data);
    }
  }, [isSuccess, connectedAthletes?.data]);

  return (
    <>
      <Paper shadow='xl' radius='md' withBorder py='sm' px={0}>
        <Stack spacing={0}>
          <Group position='apart' px='md' pb='sm'>
            <Title order={4}>Connected Profiles</Title>
            <Menu
              position='bottom-end'
              target={
                <Button compact disabled={!permission}>
                  <IconPlus size={theme.fontSizes.md} />
                </Button>
              }
              items={[
                {
                  title: 'Add Linked Profile',
                  icon: <IconEdit size={theme.fontSizes.sm} data-cy='addLinkedIcon' />,
                  onClick: () => openCreateLinkedProfileForm(),
                },
                {
                  title: 'Add Dependent Profile',
                  icon: <IconEdit size={theme.fontSizes.sm} data-cy='addDependentIcon' />,
                  onClick: () => openCreateDependentProfileForm(),
                },
              ]}
            />
          </Group>
          <Divider />
          <Table
            tableData={data}
            columns={columns}
            isSuccess={true}
            isLoading={false}
            isError={false}
            margin={8}
          />
        </Stack>
      </Paper>
      <DrawerAthleteForm
        type={
          isCreateLinkedProfileFormOpened
            ? 'create-linked'
            : isCreateDependentProfileFormOpened
            ? 'create-dependent'
            : 'closed'
        }
        opened={isCreateLinkedProfileFormOpened || isCreateDependentProfileFormOpened}
        closeHandler={
          isCreateLinkedProfileFormOpened ? closeCreateLinkedProfileForm : closeCreateDependentProfileForm
        }
        athleteId={selectedAthlete?.athleteId}
        primaryAthleteId={athlete.athleteId}
      />
      <MakePrimaryModal
        athlete={selectedAthlete}
        opened={isMakePrimaryModalOpen}
        closeHandler={closeMakePrimaryModal}
      />
      <DependentToLinkedModal
        athlete={selectedAthlete}
        opened={isDependentToLinkedModalOpen}
        closeHandler={closeDependentToLinkedModal}
      />
      <LinkedToDependentModal
        athlete={selectedAthlete}
        opened={isLinkedToDependentModalOpen}
        closeHandler={closeLinkedToDependentModal}
      />
      <DisconnectProfileModal
        athlete={selectedAthlete}
        opened={isDisconnectModalOpen}
        closeHandler={closeDisconnectModal}
      />
    </>
  );
};

export default ConnectedProfilesCard;
